import { getAccessToken } from '@/auth/getAccessToken';

// Cliente propio de la exploración de láminas.
//
// NO vive en `api/client.ts` a propósito: Daniel pidió el 15-oct-2026 que esto
// sea una exploración aislada del editor vectorial de /planta. Repite las
// ~20 líneas de `request` en vez de importarlas para que el día que se
// descarte, se borren tres archivos y `client.ts` nunca se haya tocado.

const BASE = `${import.meta.env.VITE_API_BASE_URL || ''}/api`;

async function pedir<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(opts.headers as Record<string, string> || {}),
  };
  const token = await getAccessToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${BASE}${path}`, { ...opts, headers });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((body as { error?: string }).error || `HTTP ${res.status}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

/**
 * Manda un FormData. Sin Content-Type a mano: el navegador tiene que poner el
 * boundary del multipart, y si se fuerza 'multipart/form-data' el boundary
 * falta y el servidor recibe un cuerpo que no puede partir.
 */
async function enviarForm<T>(path: string, cuerpo: FormData): Promise<T> {
  const headers: Record<string, string> = {};
  const token = await getAccessToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${BASE}${path}`, { method: 'POST', body: cuerpo, headers });
  if (!res.ok) {
    const b = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((b as { error?: string }).error || `HTTP ${res.status}`);
  }
  return res.json();
}

// ── Tipos ───────────────────────────────────────────────────────────────────

export interface PlanoLista {
  id: number;
  nombre: string;
  carpeta: string | null;
  /** false = el nombre lo propuso el OCR del rótulo y nadie lo revisó. */
  nombreConfirmado: boolean;
  versiones: number;
  hojaVigenteId: number | null;
  urlMiniatura: string | null;
  fechaActualizacion: string;
}

export interface Hoja {
  id: number;
  planoId: number;
  version: number;
  nombreArchivo: string | null;
  anchoPx: number;
  altoPx: number;
  rotacionGrados: number;
  /** El puente píxeles↔metros. Sin esto se puede ver y anotar, pero no medir. */
  metrosPorPixel: number | null;
  /**
   * De dónde salió la medida: `'rotulo'` la leyó el ingestor del cajetín,
   * `'manual'` la fijó alguien con dos puntos sobre el plano. `null` es
   * ambiguo A PROPÓSITO — o la hoja no está calibrada, o se calibró antes de
   * que existiera el dato (ver la migración 031): ahí la banda se comporta
   * como siempre en vez de inventar una procedencia.
   */
  origenCalibracion: 'rotulo' | 'manual' | null;
  /** El 1:N que se leyó del cajetín. Solo viaja con `origenCalibracion==='rotulo'`. */
  escalaCajetin: number | null;
  teselada: boolean;
  tileSize: number | null;
  /** La pirámide como lista corta, p.ej. "0,2". El nivel intermedio lo escala el navegador. */
  nivelesTiles: string | null;
  baseUrlTiles: string | null;
  /**
   * Firma de lectura que va al FINAL de cada tile (`?sv=…`). Viaja aparte de
   * `baseUrlTiles` porque el visor completa el nombre con `_{z}_{x}_{y}.jpg`:
   * una query metida en el prefijo quedaría en medio de la URL. Null cuando
   * las tiles se sirven desde la propia API (el ingestor local).
   */
  tokenTiles: string | null;
  urlMiniatura: string | null;
  urlOriginal: string | null;
  fechaCreacion: string;
}

/** Una anotación. Todo el dibujo vive en `data` como GeoJSON Feature. */
export interface Marca {
  id: number;
  hojaId: number;
  data: string;
  privada: boolean;
  fechaActualizacion: string;
}

export interface Vinculo {
  id: number;
  posX: number;
  posY: number;
  descripcion: string | null;
  planoDestinoId: number | null;
  urlAdjunto: string | null;
  color: string | null;
  automatico: boolean;
}

export interface Chinche {
  id: number;
  planoId: number;
  posX: number;
  posY: number;
  posZ: number;
  titulo: string;
  color: string | null;
  prioridad: number;
  estado: string | null;
  tareaId: number | null;
}

/**
 * Una foto de obra clavada a un punto del plano (F-4). Cuelga del PLANO, igual
 * que el chinche: la foto es del LUGAR y sobrevive al cambio de versión.
 */
export interface Foto {
  id: number;
  planoId: number;
  posX: number;
  posY: number;
  titulo: string | null;
  /** Ya viene absoluta y firmada: un `<img>` no puede mandar el Bearer. */
  url: string | null;
  /**
   * Las anotaciones dibujadas ENCIMA de la foto (F-10), como array JSON de
   * `MarcaData` — la misma forma que `Marca.data` guarda de a una, por eso las
   * pinta el mismo `CapaMarcas`. `null` = foto sin anotar.
   *
   * Es VECTORIAL por decisión de Daniel (20-oct-2026): la imagen original no se
   * reescribe nunca, así que el dibujo queda editable para siempre.
   */
  marcasJson: string | null;
  /** La fecha del archivo, que en una foto de celular es cuando se tomó. */
  fechaTomada: string | null;
  fechaCreacion: string;
}

/** Una lámina que salió de una página del PDF subido. */
export interface LaminaIngestada {
  planoId: number;
  hojaId: number;
  nombre: string;
  carpeta: string | null;
  codigo: string | null;
  /** false = no se pudo leer el cajetín; el nombre hay que ponerlo a mano. */
  rotuloLeido: boolean;
  /** false = sin escala; se puede ver y anotar, pero no medir. */
  calibrada: boolean;
  tiles: number;
  /** true = ya existía una lámina con ese código y esto entró como versión nueva. */
  esVersionNueva: boolean;
}

export interface Subida {
  id: number;
  archivo: string;
  estado: 'procesando' | 'lista' | 'error';
  /** null hasta que se abre el PDF y se cuentan las páginas. */
  paginasTotal: number | null;
  paginasListas: number;
  laminas: LaminaIngestada[];
  error: string | null;
  bytes: number;
}

export interface PlanoDetalle {
  id: number;
  nombre: string;
  carpeta: string | null;
  descripcion: string | null;
  hojas: Hoja[];
  chinches: Chinche[];
  fotos: Foto[];
}

export interface HojaConCapas {
  hoja: Hoja;
  marcas: Marca[];
  vinculos: Vinculo[];
}

// La forma medida en el tráfico real de Fieldwire: un GeoJSON Feature con el
// estilo en `properties` y las coordenadas en PÍXELES CRUDOS de la hoja (no
// normalizadas 0–1). El valor de la medida no viaja: se recalcula.
export type EstiloMarca =
  | 'rectangle' | 'polygon' | 'drawing' | 'symbol'
  | 'measurementline' | 'measurementmultiline' | 'text' | 'arrow';

export interface MarcaData {
  type: 'Feature';
  properties: {
    style: EstiloMarca;
    color?: string;
    width?: number;
    fill?: string;
    description?: string;
    fontSize?: number;
    measurement?: boolean;
    layerType?: string;
    symbolId?: string;
    // Las tres de abajo son NUESTRAS, no venían en el tráfico de Fieldwire.
    // Van en castellano para que se distingan de un vistazo de las medidas, y
    // son todas opcionales: una marca vieja sin ellas se pinta igual que antes.
    /** Flecha con punta en los DOS extremos. */
    doblePunta?: boolean;
    /** 0–1. La usa el marcador resaltador para dejar ver el plano debajo. */
    opacidad?: number;
    /** Nota de texto dentro de un recuadro, como el cuadro de texto de Procore. */
    conRecuadro?: boolean;
  };
  geometry: {
    type: 'Point' | 'LineString' | 'Polygon' | 'MultiLineString';
    coordinates: number[] | number[][] | number[][][];
  };
}

export function parsearMarca(m: Marca): MarcaData | null {
  try { return JSON.parse(m.data) as MarcaData; } catch { return null; }
}

// ── Llamadas ────────────────────────────────────────────────────────────────

export const apiLaminas = {
  lista: (idCaso?: number) =>
    pedir<PlanoLista[]>(`/laminas${idCaso ? `?caso=${idCaso}` : ''}`),

  plano: (id: number) => pedir<PlanoDetalle>(`/laminas/${id}`),

  hoja: (idHoja: number) => pedir<HojaConCapas>(`/laminas/hojas/${idHoja}`),

  /**
   * Calibra la hoja: se manda el trazo de referencia en píxeles y cuánto mide
   * de verdad, y el backend hace la división. `metros = null` descalibra.
   */
  calibrar: (idHoja: number, distanciaPx: number, metros: number | null) =>
    pedir<{ id: number; metrosPorPixel: number | null }>(
      `/laminas/hojas/${idHoja}/calibracion`,
      { method: 'PATCH', body: JSON.stringify({ distanciaPx, distanciaMetros: metros }) },
    ),

  crearMarca: (idHoja: number, data: MarcaData, privada = false) =>
    pedir<Marca>(`/laminas/hojas/${idHoja}/marcas`, {
      method: 'POST',
      body: JSON.stringify({ data: JSON.stringify(data), privada }),
    }),

  actualizarMarca: (id: number, data: MarcaData, privada = false) =>
    pedir<Marca>(`/laminas/marcas/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ data: JSON.stringify(data), privada }),
    }),

  borrarMarca: (id: number) =>
    pedir<void>(`/laminas/marcas/${id}`, { method: 'DELETE' }),

  /** Sube UN PDF. Devuelve 202 enseguida: el teselado sigue en el servidor. */
  subir: (archivo: File) => {
    const cuerpo = new FormData();
    cuerpo.append('archivo', archivo);
    return enviarForm<Subida>('/laminas/subidas', cuerpo);
  },

  subida: (id: number) => pedir<Subida>(`/laminas/subidas/${id}`),

  /** Confirmar/corregir el nombre propuesto por el cajetín. */
  renombrar: (idPlano: number, cambios: { nombre?: string; carpeta?: string | null }) =>
    pedir<PlanoDetalle>(`/laminas/${idPlano}`, {
      method: 'PATCH',
      body: JSON.stringify({ ...cambios, nombreConfirmado: true }),
    }),

  /**
   * Un salto desde un punto de esta hoja. El destino es una lámina O un
   * archivo, nunca los dos: el backend y el CHECK de la tabla lo exigen.
   */
  crearVinculo: (idHoja: number, v: Omit<Vinculo, 'id' | 'automatico'>) =>
    pedir<Vinculo>(`/laminas/hojas/${idHoja}/vinculos`, {
      method: 'POST',
      body: JSON.stringify(v),
    }),

  borrarVinculo: (id: number) =>
    pedir<void>(`/laminas/vinculos/${id}`, { method: 'DELETE' }),

  chinches: (idPlano: number) => pedir<Chinche[]>(`/laminas/${idPlano}/chinches`),

  crearChinche: (idPlano: number, c: Omit<Chinche, 'id' | 'planoId'>) =>
    pedir<Chinche>(`/laminas/${idPlano}/chinches`, {
      method: 'POST',
      body: JSON.stringify(c),
    }),

  /** Despinchar (F-8): saca el chinche del plano. NO toca la tarea. */
  borrarChinche: (id: number) =>
    pedir<void>(`/laminas/chinches/${id}`, { method: 'DELETE' }),

  fotos: (idPlano: number) => pedir<Foto[]>(`/laminas/${idPlano}/fotos`),

  /**
   * Clava una foto en un punto del plano. El archivo va por multipart y el
   * punto en campos de texto del mismo FormData: en base64 el cuerpo se infla
   * un 33% y la foto queda dos veces en memoria.
   */
  subirFoto: (idPlano: number, archivo: File,
              punto: { posX: number; posY: number }, titulo?: string) => {
    const cuerpo = new FormData();
    cuerpo.append('archivo', archivo);
    cuerpo.append('posX', String(punto.posX));
    cuerpo.append('posY', String(punto.posY));
    if (titulo) cuerpo.append('titulo', titulo);
    // La fecha del archivo es lo más cercano a «cuándo se tomó» sin leer EXIF.
    if (archivo.lastModified) {
      cuerpo.append('fechaTomada', new Date(archivo.lastModified).toISOString());
    }
    return enviarForm<Foto>(`/laminas/${idPlano}/fotos`, cuerpo);
  },

  borrarFoto: (id: number) =>
    pedir<void>(`/laminas/fotos/${id}`, { method: 'DELETE' }),

  /**
   * Guarda las anotaciones dibujadas sobre la foto (F-10). Va el dibujo
   * COMPLETO, no un delta: el editor trabaja la lista entera en memoria y
   * guarda una vez, igual que el PUT de marca reescribe la fila entera.
   */
  guardarMarcasFoto: (id: number, marcas: MarcaData[]) =>
    pedir<Foto>(`/laminas/fotos/${id}/marcas`, {
      method: 'PUT',
      body: JSON.stringify({ marcasJson: JSON.stringify(marcas) }),
    }),
};

/** Las anotaciones de una foto, tolerante a un JSON roto: sin dibujo se abre igual. */
export function parsearMarcasFoto(f: Foto): MarcaData[] {
  if (!f.marcasJson) return [];
  try {
    const x = JSON.parse(f.marcasJson);
    return Array.isArray(x) ? x as MarcaData[] : [];
  } catch { return []; }
}
